import { useState } from 'react'

import { StoryblokCallToAction } from 'components/blocks/StoryblokCallToAction'
import { StoryblokImage } from 'components/blocks/StoryblokImage'
import { Icon, Tag } from 'components/ui'
import VideoOverlay from 'components/ui/VideoOverlay'
import { storyblokEditable } from 'lib/storyblok'
import { NewHeroStoryblok } from 'lib/storyblok/types'
import { isSourceType } from 'lib/utils/isSourceType'

type Props = {
  block: NewHeroStoryblok
}

export const NewHero = ({ block }: Props) => {
  const { tag, title, description, image, media, ctas } = block
  const [videoOverlay, setVideoOverlay] = useState<string | null>(null)

  return (
    <section className="bg-background" {...storyblokEditable(block)}>
      {videoOverlay && (
        <VideoOverlay
          videoUrl={videoOverlay}
          setVideoOverlay={setVideoOverlay}
        />
      )}

      <div className="container mx-auto max-w-screen-desktop px-5 py-10 text-foreground lg:px-20 lg:py-15">
        <div className="flex flex-col justify-between gap-10 lg:flex-row lg:gap-10 xl:gap-20">
          <div className="flex w-fit flex-col justify-between gap-10 lg:max-w-[570px]">
            <div className="space-y-4">
              {tag && <Tag>{tag}</Tag>}

              <h1 className="text-title-card font-regular lg:max-w-[501px] lg:text-display">
                {title}
              </h1>
            </div>

            <div className="space-y-10">
              <p className="text-eighteen lg:max-w-[360px] xl:text-twenty">
                {description}
              </p>

              {ctas && (
                <div className="flex flex-col gap-3 lg:flex-row">
                  {ctas.map((cta) => {
                    return <StoryblokCallToAction cta={cta} key={cta._uid} />
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="aspect-rectangle overflow-hidden rounded-bl-12 rounded-tr-12 lg:w-[630px] lg:rounded-bl-15 lg:rounded-tr-15">
            {image && (!media || media?.length === 0) && (
              <StoryblokImage
                asset={image}
                className="h-full w-full object-cover"
              />
            )}
            {media &&
              media.length > 0 &&
              media.map((asset, i) =>
                isSourceType(asset.media.filename, 'image') ? (
                  <StoryblokImage
                    asset={asset.media}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div key={i} className="group relative h-full w-full">
                    <video
                      muted
                      playsInline
                      loop
                      autoPlay
                      disablePictureInPicture
                      className="h-full w-full object-cover"
                    >
                      <source src={asset.media.filename} />
                    </video>
                    <button
                      onClick={() =>
                        asset.video_youtube_link
                          ? setVideoOverlay(asset.video_youtube_link)
                          : setVideoOverlay(asset.media.filename)
                      }
                      className="absolute right-2 bottom-2 rounded-full bg-white/80 opacity-100 md:opacity-0 md:transition-opacity md:hover:bg-white md:group-hover:opacity-100 flex items-center justify-center p-2 border-none"
                      aria-label="Open video"
                    >
                      <Icon icon="expand" size={16} />
                    </button>
                  </div>
                )
              )}
          </div>
        </div>
      </div>
    </section>
  )
}
